import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";
import "lenis/dist/lenis.css";
import * as THREE from "three";

gsap.registerPlugin(ScrollTrigger);

const root = document.documentElement;
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const isEnglish = document.documentElement.lang === "en";
const translations = {
  es: {
    registerResolved: "ÍNDICE / AUTOR",
    identityResolved: "TÉRRIZO AGUIMOR / MARIO GUTIÉRREZ",
    compressing: "ANAGRAMA / COMPRIMIENDO",
    indexingSource: "14 GLYPHS / INDEXANDO FUENTE",
    reindexing: "ANAGRAMA / REINDEXANDO",
    resolvingAuthor: "AUTOR / RESOLVIENDO",
    authorFound: "MARIO GUTIÉRREZ / AUTOR ENCONTRADO",
    closeIndex: "Cerrar índice",
    openIndex: "Abrir índice",
  },
  en: {
    registerResolved: "INDEX / AUTHOR",
    identityResolved: "TÉRRIZO AGUIMOR / MARIO GUTIÉRREZ",
    compressing: "ANAGRAM / COMPRESSING",
    indexingSource: "14 GLYPHS / INDEXING SOURCE",
    reindexing: "ANAGRAM / REINDEXING",
    resolvingAuthor: "AUTHOR / RESOLVING",
    authorFound: "MARIO GUTIÉRREZ / AUTHOR FOUND",
    closeIndex: "Close index",
    openIndex: "Open index",
  },
};
const copy = translations[isEnglish ? "en" : "es"];
const targetOrder = [11, 7, 2, 4, 6, 8, 9, 0, 10, 1, 3, 13, 12, 5];
let lenis;
let webglDispose = () => {};

window.__terrizoRuntime = {
  intro: "pending",
  lenis: "pending",
  navigation: "pending",
  scroll: "pending",
  gsap: "active",
  webgl: "pending",
  pliegors: true,
  pliegocss: true,
};

function tweenTo(target, vars) {
  return new Promise((resolve) => {
    gsap.to(target, { ...vars, onComplete: resolve });
  });
}

async function compileIdentity() {
  const compositor = document.querySelector("[data-identity-word]");
  const source = document.querySelector("[data-identity-source]");
  const register = document.querySelector("[data-identity-register]");
  const registerStatus = document.querySelector(
    "[data-identity-register-status]",
  );
  const correction = document.querySelector("[data-identity-correction]");
  const target = document.querySelector("[data-identity-target]");
  const kicker = document.querySelector("[data-identity-kicker]");
  const resolved = document.querySelector("[data-identity-resolved]");
  const details = document.querySelectorAll("[data-identity-detail]");
  const corners = document.querySelectorAll(".hero-corner");
  const canvas = document.querySelector("[data-webgl-canvas]");
  const scrollPrompt = document.querySelector(".hero-scroll");

  if (
    !compositor ||
    !source ||
    !register ||
    !target ||
    !kicker ||
    !resolved
  ) {
    root.dataset.loading = "false";
    window.__terrizoRuntime.intro = "missing";
    return;
  }

  const letters = [...register.querySelectorAll(".identity-letter")];
  const byToken = new Map(
    letters.map((letter) => [Number(letter.dataset.token), letter]),
  );
  const resolvedLetters = targetOrder
    .map((token) => byToken.get(token))
    .filter(Boolean);
  const correctionFace = byToken.get(12)?.querySelector(
    ".identity-letter-face",
  );
  const targetLines = target.querySelectorAll(
    ".identity-target-given, .identity-target-family",
  );
  const animatedTargets = [
    source,
    register,
    correction,
    target,
    ...letters,
    ...targetLines,
    kicker,
    resolved,
    ...details,
    ...corners,
  ].filter(Boolean);
  let interrupted = false;
  let settled = false;

  root.dataset.introPhase = "boot";
  scrollPrompt?.setAttribute("tabindex", "-1");

  const settleFinal = (runtimeState = "complete") => {
    if (settled) return;
    settled = true;
    register.classList.add("is-resolved");
    if (correctionFace) correctionFace.textContent = "E";
    if (registerStatus) registerStatus.textContent = copy.registerResolved;
    kicker.textContent = copy.identityResolved;
    gsap.set(source, {
      opacity: 0,
      visibility: "hidden",
      clipPath: "inset(50% 0 50% 0)",
    });
    gsap.set(register, {
      opacity: 0,
      visibility: "hidden",
      scaleX: 0.88,
    });
    gsap.set(target, {
      opacity: 1,
      visibility: "visible",
      clipPath: "inset(0% 0 0% 0)",
      filter: "blur(0px)",
    });
    gsap.set(targetLines, { yPercent: 0, opacity: 1 });
    gsap.set([kicker, resolved, ...details, ...corners], {
      opacity: 1,
      y: 0,
    });
    gsap.set(kicker, { color: "#f1eee6" });
    if (canvas) gsap.set(canvas, { opacity: 0.28 });
    scrollPrompt?.removeAttribute("tabindex");
    root.dataset.introPhase = "complete";
    root.dataset.loading = "false";
    root.dataset.runtime = runtimeState === "complete" ? "active" : "degraded";
    window.__terrizoRuntime.intro = runtimeState;
  };

  const onViewportChange = () => {
    interrupted = true;
    for (const tween of gsap.getTweensOf(animatedTargets)) {
      tween.progress(1);
    }
    settleFinal();
  };

  try {
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
    await new Promise((resolveFrame) => requestAnimationFrame(resolveFrame));

    if (
      reduceMotion ||
      (window.location.hash && window.location.hash !== "#inicio")
    ) {
      settleFinal();
      return;
    }

    window.addEventListener("resize", onViewportChange);
    window.addEventListener("orientationchange", onViewportChange);

    gsap.set(source, {
      opacity: 1,
      visibility: "visible",
      clipPath: "inset(50% 0 50% 0)",
      filter: "blur(8px)",
      y: 18,
      scale: 0.985,
    });
    gsap.set(register, {
      opacity: 0,
      visibility: "visible",
      scaleX: 0.72,
      filter: "blur(5px)",
    });
    gsap.set(letters, { opacity: 0, y: 9 });
    gsap.set(correction, { opacity: 0, y: 6 });
    gsap.set(target, {
      opacity: 0,
      visibility: "visible",
      clipPath: "inset(50% 0 50% 0)",
      filter: "blur(7px)",
    });
    gsap.set(targetLines, { yPercent: 22, opacity: 0 });

    root.dataset.introPhase = "source";
    await Promise.all([
      tweenTo(source, {
        clipPath: "inset(0% 0 0% 0)",
        filter: "blur(0px)",
        y: 0,
        scale: 1,
        duration: 0.88,
        ease: "expo.out",
      }),
      canvas
        ? tweenTo(canvas, {
            opacity: 0.76,
            duration: 1.06,
            ease: "power2.out",
          })
        : Promise.resolve(),
      tweenTo(corners, {
        opacity: 1,
        duration: 0.7,
        stagger: 0.055,
        ease: "power2.out",
      }),
      tweenTo(kicker, {
        opacity: 1,
        y: 0,
        duration: 0.68,
        ease: "power3.out",
      }),
    ]);
    if (interrupted) return;
    await tweenTo({}, { duration: 0.42 });
    if (interrupted) return;

    root.dataset.introPhase = "collapse";
    kicker.textContent = copy.compressing;
    await tweenTo(source, {
      clipPath: "inset(49.7% 12% 49.7% 12%)",
      opacity: 0,
      scaleX: 0.78,
      filter: "blur(4px)",
      duration: 0.44,
      ease: "expo.inOut",
    });
    if (interrupted) return;
    gsap.set(source, { visibility: "hidden" });

    root.dataset.introPhase = "register";
    kicker.textContent = copy.indexingSource;
    await Promise.all([
      tweenTo(register, {
        opacity: 1,
        scaleX: 1,
        filter: "blur(0px)",
        duration: 0.46,
        ease: "expo.out",
      }),
      tweenTo(letters, {
        opacity: 1,
        y: 0,
        duration: 0.36,
        stagger: 0.022,
        ease: "power3.out",
      }),
    ]);
    if (interrupted) return;
    await tweenTo({}, { duration: 0.26 });
    if (interrupted) return;

    root.dataset.introPhase = "proof";
    kicker.textContent = copy.reindexing;
    await tweenTo(resolvedLetters, {
      opacity: 0.08,
      y: 8,
      duration: 0.2,
      stagger: 0.016,
      ease: "power2.in",
    });
    if (interrupted) return;

    register.classList.add("is-resolved");
    if (correctionFace) correctionFace.textContent = "E";
    if (registerStatus) registerStatus.textContent = copy.registerResolved;
    gsap.set(resolvedLetters, { opacity: 0.08, y: -8 });
    await Promise.all([
      tweenTo(resolvedLetters, {
        opacity: 1,
        y: 0,
        duration: 0.38,
        stagger: 0.022,
        ease: "power3.out",
      }),
      tweenTo(correction, {
        opacity: 1,
        y: 0,
        duration: 0.42,
        delay: 0.16,
        ease: "power3.out",
      }),
    ]);
    if (interrupted) return;
    await tweenTo({}, { duration: 0.34 });
    if (interrupted) return;

    root.dataset.introPhase = "resolve";
    kicker.textContent = copy.resolvingAuthor;
    await tweenTo(register, {
      opacity: 0,
      scaleX: 0.86,
      filter: "blur(6px)",
      duration: 0.36,
      ease: "expo.in",
    });
    if (interrupted) return;
    gsap.set(register, { visibility: "hidden" });

    root.dataset.introPhase = "target";
    kicker.textContent = copy.authorFound;
    await Promise.all([
      tweenTo(target, {
        opacity: 1,
        clipPath: "inset(0% 0 0% 0)",
        filter: "blur(0px)",
        duration: 0.72,
        ease: "expo.inOut",
      }),
      tweenTo(targetLines, {
        yPercent: 0,
        opacity: 1,
        duration: 0.66,
        stagger: 0.07,
        ease: "expo.out",
      }),
      tweenTo(kicker, {
        color: "#f1eee6",
        duration: 0.54,
        ease: "power2.inOut",
      }),
    ]);
    if (interrupted) return;

    await Promise.all([
      tweenTo(resolved, {
        y: 0,
        opacity: 1,
        duration: 0.52,
        ease: "power3.out",
      }),
      tweenTo(details, {
        y: 0,
        opacity: 1,
        duration: 0.48,
        stagger: 0.07,
        ease: "power3.out",
      }),
    ]);
    if (interrupted) return;
    settleFinal();
  } catch (error) {
    settleFinal("degraded");
    console.warn("[terrizo] intro degraded", error);
  } finally {
    window.removeEventListener("resize", onViewportChange);
    window.removeEventListener("orientationchange", onViewportChange);
  }
}

function initializeLenis() {
  if (reduceMotion) {
    window.__terrizoRuntime.lenis = "reduced-motion";
    return undefined;
  }

  lenis = new Lenis({
    duration: 1.15,
    smoothWheel: true,
    anchors: {
      offset: 0,
    },
  });

  lenis.on("scroll", ScrollTrigger.update);
  gsap.ticker.add((time) => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);
  window.__terrizoRuntime.lenis = "active";
  return lenis;
}

function initializeNavigation() {
  const brand = document.querySelector(".navigation-brand");
  const toggle = document.querySelector("[data-menu-toggle]");
  const menu = document.querySelector("[data-site-menu]");
  const main = document.querySelector("[data-site-main]");
  if (!toggle || !menu) {
    window.__terrizoRuntime.navigation = "missing";
    return;
  }

  const links = [...menu.querySelectorAll("a[href]")];
  const focusables = [brand, toggle, ...links].filter(Boolean);
  let open = false;

  const setSemantics = (isOpen) => {
    toggle.setAttribute("aria-expanded", String(isOpen));
    toggle.setAttribute(
      "aria-label",
      isOpen ? copy.closeIndex : copy.openIndex,
    );
    menu.setAttribute("aria-hidden", String(!isOpen));
    if (isOpen) {
      menu.removeAttribute("inert");
    } else {
      menu.setAttribute("inert", "");
    }
    main?.toggleAttribute("inert", isOpen);
  };

  const closeMenu = ({ restoreFocus = false } = {}) => {
    if (!open) return;
    open = false;
    gsap.killTweensOf(menu);
    if (restoreFocus || menu.contains(document.activeElement)) {
      toggle.focus({ preventScroll: true });
    }
    setSemantics(false);
    gsap.to(menu, {
      clipPath: "inset(0 0 100% 0)",
      opacity: 0,
      duration: reduceMotion ? 0.01 : 0.62,
      ease: "power4.inOut",
      onComplete: () => {
        if (open) return;
        gsap.set(menu, { visibility: "hidden" });
        root.dataset.menuOpen = "false";
        lenis?.start();
      },
    });
  };

  const openMenu = () => {
    if (open) return;
    open = true;
    gsap.killTweensOf(menu);
    setSemantics(true);
    root.dataset.menuOpen = "true";
    lenis?.stop();
    menu.scrollTop = 0;
    gsap.set(menu, { visibility: "visible" });
    gsap.to(menu, {
      clipPath: "inset(0 0 0% 0)",
      opacity: 1,
      duration: reduceMotion ? 0.01 : 0.72,
      ease: "power4.inOut",
    });
  };

  toggle.addEventListener("click", () => (open ? closeMenu() : openMenu()));
  brand?.addEventListener("click", () => closeMenu());
  links.forEach((link) => link.addEventListener("click", () => closeMenu()));
  document.addEventListener("keydown", (event) => {
    if (!open) return;
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu({ restoreFocus: true });
      return;
    }
    if (event.key !== "Tab" || focusables.length === 0) return;
    const firstFocusable = focusables[0];
    const lastFocusable = focusables.at(-1);
    if (event.shiftKey && document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable?.focus();
    } else if (!event.shiftKey && document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable?.focus();
    }
  });

  root.dataset.menuOpen = "false";
  setSemantics(false);
  gsap.set(menu, {
    visibility: "hidden",
    clipPath: "inset(0 0 100% 0)",
    opacity: 0,
  });
  window.__terrizoRuntime.navigation = "active";
}

function initializeScrollCinema() {
  if (reduceMotion) {
    ScrollTrigger.refresh();
    return;
  }

  gsap.to(".hero-webgl", {
    scale: 1.14,
    opacity: 0.34,
    ease: "none",
    scrollTrigger: {
      trigger: ".cinema-hero",
      start: "top top",
      end: "bottom top",
      scrub: 1.2,
    },
  });

  gsap.to(".identity-compositor", {
    yPercent: -20,
    scale: 1.16,
    opacity: 0.06,
    ease: "none",
    scrollTrigger: {
      trigger: ".cinema-hero",
      start: "24% top",
      end: "bottom 18%",
      scrub: 1,
    },
  });

  gsap.to(".hero-resolved, .hero-equation, .hero-scroll", {
    yPercent: -55,
    opacity: 0,
    ease: "none",
    scrollTrigger: {
      trigger: ".cinema-hero",
      start: "18% top",
      end: "70% top",
      scrub: 1,
    },
  });

  const identityTimeline = gsap.timeline({
    scrollTrigger: {
      trigger: ".identity-sequence",
      start: "top top",
      end: "bottom bottom",
      scrub: 1.15,
    },
  });
  identityTimeline
    .to(".identity-image", {
      scale: 1,
      filter: "grayscale(0.3) contrast(1.18) brightness(0.7)",
      duration: 0.55,
      ease: "none",
    })
    .to(
      ".identity-scan",
      {
        clipPath: "inset(0 0% 0 0)",
        duration: 0.4,
        ease: "none",
      },
      0.1,
    )
    .fromTo(
      ".identity-copy > *",
      { y: 55, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.35,
        stagger: 0.07,
        ease: "power2.out",
      },
      0.24,
    );

  const mapTimeline = gsap.timeline({
    scrollTrigger: {
      trigger: ".system-map-field",
      start: "top 76%",
      toggleActions: "play none none none",
    },
  });
  mapTimeline
    .from(".map-spine", {
      scaleY: 0,
      opacity: 0,
      transformOrigin: "center center",
      duration: 0.9,
      ease: "power3.inOut",
    })
    .from(
      ".map-node",
      {
        scale: 0.72,
        y: 55,
        opacity: 0,
        duration: 0.82,
        stagger: 0.09,
        ease: "expo.out",
      },
      0.18,
    );

  gsap.utils.toArray(".project-dossier").forEach((dossier) => {
    const identity = dossier.querySelectorAll(
      ".dossier-identity > span, .dossier-identity > p, .dossier-identity h3, .dossier-identity blockquote, .dossier-link",
    );
    const body = dossier.querySelectorAll(
      ".dossier-question, .dossier-fold, .dossier-verdict",
    );
    const architecture = dossier.querySelectorAll(".dossier-architecture li");
    const dossierTimeline = gsap.timeline({
      scrollTrigger: {
        trigger: dossier,
        start: "top 66%",
        toggleActions: "play none none none",
      },
    });

    dossierTimeline
      .from(identity, {
        x: -70,
        opacity: 0,
        duration: 0.78,
        stagger: 0.055,
        ease: "power4.out",
      })
      .from(
        body,
        {
          y: 70,
          opacity: 0,
          duration: 0.72,
          stagger: 0.07,
          ease: "power3.out",
        },
        0.16,
      )
      .from(
        architecture,
        {
          y: 30,
          opacity: 0,
          duration: 0.55,
          stagger: 0.06,
          ease: "power2.out",
        },
        0.34,
      );
  });

  gsap.utils.toArray(".fault-line").forEach((line, index) => {
    gsap.from(line.children, {
      x: index % 2 === 0 ? -75 : 75,
      opacity: 0,
      duration: 0.88,
      stagger: 0.07,
      ease: "power4.out",
      scrollTrigger: {
        trigger: line,
        start: "top 78%",
        toggleActions: "play none none none",
      },
    });
  });

  gsap.from(".research-record", {
    y: 90,
    clipPath: "inset(0 0 100% 0)",
    opacity: 0,
    duration: 1,
    stagger: 0.16,
    ease: "expo.out",
    scrollTrigger: {
      trigger: ".research-ledger",
      start: "top 76%",
      toggleActions: "play none none none",
    },
  });

  const methodTimeline = gsap.timeline({
    scrollTrigger: {
      trigger: ".method-section",
      start: "top top",
      end: "bottom bottom",
      scrub: 1.15,
    },
  });
  methodTimeline
    .to(".method-mark", {
      rotation: 354,
      scale: 0.78,
      duration: 1,
      ease: "none",
    })
    .fromTo(
      ".method-copy > *",
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        stagger: 0.07,
        duration: 0.36,
        ease: "power2.out",
      },
      0.12,
    );

  gsap.from(".manifesto-line", {
    scale: 1.3,
    yPercent: 22,
    opacity: 0,
    duration: 1.35,
    ease: "expo.out",
    scrollTrigger: {
      trigger: ".manifesto-section",
      start: "top 72%",
      toggleActions: "play none none none",
    },
  });

  gsap.utils.toArray(".footer-links a").forEach((link) => {
    gsap.from(link, {
      x: 45,
      opacity: 0,
      duration: 0.75,
      ease: "power3.out",
      scrollTrigger: {
        trigger: link,
        start: "top 92%",
        toggleActions: "play none none none",
      },
    });
  });

  ScrollTrigger.refresh();
}

function initializeWebGL() {
  const canvas = document.querySelector("[data-webgl-canvas]");
  const hero = document.querySelector("[data-hero]");
  if (!canvas || !hero || reduceMotion) {
    root.dataset.webgl = reduceMotion ? "reduced-motion" : "missing";
    window.__terrizoRuntime.webgl = root.dataset.webgl;
    return;
  }

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      powerPreference: "high-performance",
    });
  } catch {
    root.dataset.webgl = "fallback";
    window.__terrizoRuntime.webgl = "fallback";
    return;
  }

  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
  renderer.setClearColor(0x000000, 0);
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  const camera = new THREE.OrthographicCamera(-8, 8, 5, -5, 0.1, 20);
  camera.position.z = 6;

  const shaderUniforms = {
    uTime: { value: 0 },
    uResolution: { value: new THREE.Vector2(1, 1) },
    uPointer: { value: new THREE.Vector2(0, 0) },
    uScroll: { value: 0 },
  };
  const shaderMaterial = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: shaderUniforms,
    vertexShader: `
      varying vec2 vUv;

      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uTime;
      uniform vec2 uResolution;
      uniform vec2 uPointer;
      uniform float uScroll;
      varying vec2 vUv;

      float hash(vec2 point) {
        return fract(sin(dot(point, vec2(127.1, 311.7))) * 43758.5453123);
      }

      float noise(vec2 point) {
        vec2 cell = floor(point);
        vec2 local = fract(point);
        local = local * local * (3.0 - 2.0 * local);
        return mix(
          mix(hash(cell), hash(cell + vec2(1.0, 0.0)), local.x),
          mix(hash(cell + vec2(0.0, 1.0)), hash(cell + vec2(1.0)), local.x),
          local.y
        );
      }

      void main() {
        float aspect = uResolution.x / max(uResolution.y, 1.0);
        vec2 point = (vUv - 0.5) * vec2(aspect, 1.0);
        point += uPointer * 0.045;
        float time = uTime * 0.08;
        float field = noise(point * 2.4 + vec2(time, -time * 0.7));
        field += noise(point * 5.2 - vec2(time * 0.42, time)) * 0.46;

        float coral = exp(-length(point - vec2(-0.58, -0.34)) * 2.7);
        float violet = exp(-length(point - vec2(0.54, 0.3)) * 3.1);
        float cyan = exp(-length(point - vec2(0.04, -0.02)) * 5.6);
        float current = smoothstep(0.34, 0.82, field + sin(point.x * 3.4 - time) * 0.08);

        vec3 color = vec3(0.004, 0.006, 0.009);
        color += vec3(1.0, 0.16, 0.07) * coral * (0.11 + current * 0.12);
        color += vec3(0.2, 0.08, 0.68) * violet * (0.16 + current * 0.16);
        color += vec3(0.02, 0.42, 0.52) * cyan * (0.08 + current * 0.08);
        color += vec3(0.035, 0.04, 0.05) * current;
        color *= 1.0 - length(vUv - 0.5) * 0.56;
        color += (hash(gl_FragCoord.xy + uTime) - 0.5) * 0.018;
        color *= 1.0 - uScroll * 0.38;

        gl_FragColor = vec4(color, 0.96);
      }
    `,
  });
  const planeGeometry = new THREE.PlaneGeometry(2, 2);
  const plane = new THREE.Mesh(planeGeometry, shaderMaterial);
  plane.position.z = -2;
  scene.add(plane);

  const rows = 58;
  const columns = 88;
  const segmentCount = rows * (columns - 1);
  const linePositions = new Float32Array(segmentCount * 6);
  const lineBases = new Float32Array(segmentCount * 6);
  let lineOffset = 0;
  for (let row = 0; row < rows; row += 1) {
    const y = -4.8 + (row / (rows - 1)) * 9.6;
    for (let column = 0; column < columns - 1; column += 1) {
      const x1 = -8.4 + (column / (columns - 1)) * 16.8;
      const x2 = -8.4 + ((column + 1) / (columns - 1)) * 16.8;
      lineBases.set([x1, y, 0, x2, y, 0], lineOffset);
      linePositions.set([x1, y, 0, x2, y, 0], lineOffset);
      lineOffset += 6;
    }
  }
  const lineGeometry = new THREE.BufferGeometry();
  const lineAttribute = new THREE.BufferAttribute(linePositions, 3);
  lineAttribute.setUsage(THREE.DynamicDrawUsage);
  lineGeometry.setAttribute("position", lineAttribute);
  const lineMaterial = new THREE.LineBasicMaterial({
    color: 0xf1eee6,
    transparent: true,
    opacity: 0.13,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const lineField = new THREE.LineSegments(lineGeometry, lineMaterial);
  lineField.position.z = -0.6;
  lineField.rotation.z = -0.045;
  scene.add(lineField);

  const particleGeometry = new THREE.BufferGeometry();
  const particleCount = 460;
  const particlePositions = new Float32Array(particleCount * 3);
  let particleSeed = 0x74a14;
  const deterministicRandom = () => {
    particleSeed = (particleSeed * 1664525 + 1013904223) >>> 0;
    return particleSeed / 0x100000000;
  };
  for (let index = 0; index < particleCount; index += 1) {
    particlePositions[index * 3] = (deterministicRandom() - 0.5) * 16.8;
    particlePositions[index * 3 + 1] = (deterministicRandom() - 0.5) * 9.6;
    particlePositions[index * 3 + 2] = deterministicRandom() * 0.5 - 0.25;
  }
  particleGeometry.setAttribute(
    "position",
    new THREE.BufferAttribute(particlePositions, 3),
  );
  const particles = new THREE.Points(
    particleGeometry,
    new THREE.PointsMaterial({
      color: 0xff6347,
      size: 0.022,
      transparent: true,
      opacity: 0.42,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  particles.position.z = -0.25;
  scene.add(particles);

  const pointer = { x: 0, y: 0 };
  const pointerTarget = { x: 0, y: 0 };
  const fieldState = { scroll: 0 };
  const onPointerMove = (event) => {
    pointerTarget.x = event.clientX / window.innerWidth - 0.5;
    pointerTarget.y = event.clientY / window.innerHeight - 0.5;
  };
  window.addEventListener("pointermove", onPointerMove, { passive: true });

  const resize = () => {
    const { width, height } = hero.getBoundingClientRect();
    const safeWidth = Math.max(width, 1);
    const safeHeight = Math.max(Math.min(height, window.innerHeight), 1);
    renderer.setSize(safeWidth, safeHeight, false);
    const aspect = safeWidth / safeHeight;
    camera.left = -aspect * 5;
    camera.right = aspect * 5;
    camera.top = 5;
    camera.bottom = -5;
    camera.updateProjectionMatrix();
    plane.scale.set(aspect * 5, 5, 1);
    const horizontalScale = Math.max(1, aspect / 1.68);
    lineField.scale.x = horizontalScale;
    particles.scale.x = horizontalScale;
    shaderUniforms.uResolution.value.set(safeWidth, safeHeight);
  };
  resize();
  window.addEventListener("resize", resize, { passive: true });

  let visible = true;
  const observer = new IntersectionObserver(
    ([entry]) => {
      visible = entry.isIntersecting;
    },
    { rootMargin: "20%" },
  );
  observer.observe(hero);

  const timer = new THREE.Timer();
  timer.connect(document);
  let frame = 0;
  const render = (timestamp) => {
    frame = requestAnimationFrame(render);
    if (!visible || document.hidden) return;
    timer.update(timestamp);
    const elapsed = timer.getElapsed();
    pointer.x += (pointerTarget.x - pointer.x) * 0.045;
    pointer.y += (pointerTarget.y - pointer.y) * 0.045;
    shaderUniforms.uTime.value = elapsed;
    shaderUniforms.uPointer.value.set(pointer.x, -pointer.y);
    shaderUniforms.uScroll.value = fieldState.scroll;

    const pointerX = pointer.x * 9;
    const pointerY = -pointer.y * 5.5;
    for (let offset = 0; offset < linePositions.length; offset += 6) {
      for (let vertex = 0; vertex < 2; vertex += 1) {
        const index = offset + vertex * 3;
        const baseX = lineBases[index];
        const baseY = lineBases[index + 1];
        const distance = Math.hypot(baseX - pointerX, baseY - pointerY);
        const current =
          Math.sin(baseX * 0.52 + elapsed * 0.28 + baseY * 0.44) * 0.105 +
          Math.cos(baseY * 1.18 - elapsed * 0.34) * 0.055 +
          Math.exp(-distance * 0.72) *
            Math.sin(distance * 3.2 - elapsed * 1.1) *
            0.24;
        linePositions[index + 1] =
          baseY + current + fieldState.scroll * baseX * 0.028;
        linePositions[index + 2] =
          Math.sin(baseX * 0.28 + baseY * 0.24 + elapsed * 0.22) * 0.12;
      }
    }
    lineAttribute.needsUpdate = true;
    lineField.rotation.z = -0.045 + pointer.x * 0.025;
    particles.rotation.z = elapsed * 0.008 - fieldState.scroll * 0.12;
    particles.position.x = pointer.x * 0.16;
    particles.position.y = -pointer.y * 0.1;
    renderer.render(scene, camera);
  };
  render();

  gsap.to(fieldState, {
    scroll: 1,
    ease: "none",
    scrollTrigger: {
      trigger: ".cinema-hero",
      start: "top top",
      end: "bottom top",
      scrub: 1.1,
    },
  });

  root.dataset.webgl = "active";
  window.__terrizoRuntime.webgl = "active";
  window.__terrizoRuntime.webglSegments = segmentCount;
  window.__terrizoRuntime.webglPoints = particleCount;

  webglDispose = () => {
    cancelAnimationFrame(frame);
    observer.disconnect();
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("resize", resize);
    planeGeometry.dispose();
    shaderMaterial.dispose();
    lineGeometry.dispose();
    lineMaterial.dispose();
    particleGeometry.dispose();
    particles.material.dispose();
    timer.dispose();
    renderer.dispose();
  };
}

function initializeSafely(subsystem, initializer) {
  try {
    const result = initializer();
    if (window.__terrizoRuntime[subsystem] === "pending") {
      window.__terrizoRuntime[subsystem] = "active";
    }
    return result;
  } catch (error) {
    window.__terrizoRuntime[subsystem] = "degraded";
    console.warn(`[terrizo] ${subsystem} degraded`, error);
    return undefined;
  }
}

async function start() {
  initializeNavigation();
  initializeSafely("lenis", initializeLenis);
  initializeSafely("webgl", initializeWebGL);
  await compileIdentity();
  initializeSafely("scroll", initializeScrollCinema);
  document.body.setAttribute("aria-busy", "false");
  window.__terrizoRuntime.ready = true;
  window.dispatchEvent(new CustomEvent("terrizo:ready"));
}

start().catch((error) => {
  root.dataset.loading = "false";
  root.dataset.runtime = "degraded";
  window.__terrizoRuntime.ready = false;
  window.__terrizoRuntime.error = error instanceof Error ? error.message : String(error);
  console.error("[terrizo] runtime degraded", error);
});

window.addEventListener(
  "pagehide",
  (event) => {
    if (event.persisted) return;
    lenis?.destroy();
    webglDispose();
  },
);
